import type { ApiErrorBody } from '@aitoken/contracts';

/** 后台接口错误：带服务端错误码与 request_id，便于在提示中给运营人员复制排查。 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly param: string | undefined;
  readonly retryable: boolean;
  readonly requestId: string | null;

  constructor(init: {
    status: number;
    code: string;
    message: string;
    param?: string | undefined;
    retryable?: boolean;
    requestId?: string | null;
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.param = init.param;
    this.retryable = init.retryable ?? false;
    this.requestId = init.requestId ?? null;
  }
}

export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}

export function isUnauthenticated(err: unknown): boolean {
  return isApiError(err) && (err.status === 401 || err.code === 'unauthenticated');
}

/* ---------------------------------------------------------------- CSRF */

let csrfToken: string | null = null;
let csrfPromise: Promise<string> | null = null;

export function setCsrfToken(token: string | null) {
  csrfToken = token;
}

async function ensureCsrf(): Promise<string> {
  if (csrfToken) return csrfToken;
  csrfPromise ??= request<{ csrf_token: string }>('GET', '/admin/me')
    .then((me) => {
      csrfToken = me.csrf_token;
      return me.csrf_token;
    })
    .finally(() => {
      csrfPromise = null;
    });
  return csrfPromise;
}

/* ---------------------------------------------------------------- request */

export type Query = Record<string, string | number | boolean | null | undefined>;

export interface RequestOptions {
  query?: Query;
  body?: unknown;
  /** 敏感写操作的“操作原因”，以 X-Admin-Reason 头提交（encodeURIComponent） */
  reason?: string;
  signal?: AbortSignal;
  /** 登录类接口不需要 CSRF */
  skipCsrf?: boolean;
}

export function buildUrl(path: string, query?: Query): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

async function parseError(res: Response): Promise<ApiError> {
  const headerRequestId = res.headers.get('x-request-id');
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  const err = (body as Partial<ApiErrorBody> | null)?.error;
  if (err && typeof err.code === 'string') {
    return new ApiError({
      status: res.status,
      code: err.code,
      message: err.message || `请求失败（${res.status}）`,
      param: err.param,
      retryable: err.retryable,
      requestId: err.request_id ?? headerRequestId,
    });
  }
  return new ApiError({
    status: res.status,
    code: res.status === 401 ? 'unauthenticated' : 'http_error',
    message: res.status >= 500 ? `服务暂时不可用（${res.status}）` : `请求失败（${res.status}）`,
    requestId: headerRequestId,
    retryable: res.status >= 500,
  });
}

async function request<T>(
  method: string,
  path: string,
  opts: RequestOptions = {},
  retried = false,
): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const isWrite = method !== 'GET' && method !== 'HEAD';
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (isWrite && !opts.skipCsrf) headers['X-CSRF-Token'] = await ensureCsrf();
  if (opts.reason) headers['X-Admin-Reason'] = encodeURIComponent(opts.reason);

  let res: Response;
  try {
    res = await fetch(buildUrl(path, opts.query), {
      method,
      headers,
      credentials: 'include',
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
      signal: opts.signal,
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiError({
      status: 0,
      code: 'network_error',
      message: '无法连接服务器，请检查网络',
      retryable: true,
    });
  }

  if (!res.ok) {
    const error = await parseError(res);
    // CSRF 令牌失效（例如会话轮换）时刷新一次再重试
    if (error.code === 'csrf_failed' && isWrite && !retried && !opts.skipCsrf) {
      csrfToken = null;
      return request<T>(method, path, opts, true);
    }
    throw error;
  }
  if (res.status === 204) return undefined as T;
  const type = res.headers.get('content-type') ?? '';
  if (!type.includes('application/json')) return (await res.text()) as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string, query?: Query, signal?: AbortSignal) => request<T>('GET', path, { query, signal }),
  post: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'body'>) =>
    request<T>('POST', path, { ...opts, body }),
  put: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'body'>) =>
    request<T>('PUT', path, { ...opts, body }),
  patch: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'body'>) =>
    request<T>('PATCH', path, { ...opts, body }),
  delete: <T>(path: string, opts?: Omit<RequestOptions, 'body'>) => request<T>('DELETE', path, opts),
};

/** 统一列表分页结构 */
export interface Page<T> {
  data: T[];
  has_more: boolean;
  next_cursor: string | null;
}

export interface ListResponse<T> {
  data: T[];
}

/** 用于提示：把错误转成“信息 + request_id”。 */
export function describeError(err: unknown): { title: string; description?: string } {
  if (isApiError(err)) {
    const parts: string[] = [];
    if (err.param) parts.push(`字段 ${err.param}`);
    if (err.requestId) parts.push(`request_id: ${err.requestId}`);
    else if (err.code && err.code !== 'http_error') parts.push(err.code);
    return { title: err.message, description: parts.join(' · ') || undefined };
  }
  if (err instanceof Error) return { title: err.message };
  return { title: '操作失败' };
}
